import {CalendarEventPriority} from "../../../shared/types/calendar-types/calendarEventPriority";
import {CalendarEventResource} from "../../../shared/types/calendar-types/calendarEventResource";
import {CalendarEventUpdateRequest} from "../../../shared/types/calendar-types/calendarEventUpdateRequest";
import {Timezone} from "@syncfusion/ej2-angular-schedule";
import {getEndDateTimeOneHourLater} from "./calendar-helper.mapper";
import {CalendarEventInventoryResource} from "../../../shared/types/calendar-types/calendarEventInventoryResource";
import {CalendarEventInventoryValidationRequestInventoriesInner} from "../../../shared/types/calendar-types/calendarEventInventoryValidationRequestInventoriesInner";


export function mapCalendarEventResourceToCalendarEventUpdateRequest(event: CalendarEventResource): CalendarEventUpdateRequest {
  const mappedData: CalendarEventUpdateRequest = {
    name: event.name ?? '',
    calendar_id: event.calendar_id ?? -1,
    description: event.description ?? '',
    priority: event.priority ?? CalendarEventPriority.Medium,
    creator: {notifications: event.creator.notifications ?? false},
    timing: event.timing,
    recurrence: event.recurrence,
    location: event.location ?? '',
    override: event.override,
    participants: {
      user_ids: event.participants.user_ids ?? [],
      group_ids: event.participants.group_ids ?? [],
      external_user_emails: event.participants.external_user_emails ?? [],
      all_users: false,
      notify_all_users: false,
      notify_user_ids: event.participants.notify_user_ids ?? [],
      notify_group_ids: event.participants.notify_group_ids ?? [],
    },
    inventories: mapInventoryResourceToInnerInventoryResource(event.inventories),
  };

  return mappedData;
}

export function createNewCalendarEventResource(recurring: boolean, date: Date): CalendarEventResource {
  const newEvent: CalendarEventResource = {
    id: -1,
    calendar_id: -1,
    name: '',
    name_translated: null,
    description: '',
    description_translated: null,
    priority: CalendarEventPriority.Medium,
    creator: {notifications: false, user_id: null},
    timing: {
      start_at: getEndDateTimeOneHourLater(date).start.toISOString(),
      duration: 60,
      is_full_day: false,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      notify_before: 5,
      notify_time: null,
    },
    recurrence: {
      rrule: recurring ? 'FREQ=DAILY;INTERVAL=1' : '',
      deleted_occurrences: [],
      overridden_occurrences: [],
    },
    override: {
      parent_id: null,
      original_at: '',
    },
    location: '',
    location_translated: null,
    permission: {
      editable: true,
      masked: true,
    },
    etag: '',
    participants: {
      all_users: false,
      notify_all_users: false,
      user_ids: [],
      group_ids: [],
      external_user_emails: [],
      notify_user_ids: [],
      notify_group_ids: [],
    },
    inventories: [],
    capacity: null,
    files: []
  }
  return newEvent;
}

export function createCalendarEventUpdateRequest(): CalendarEventUpdateRequest {
  const newEvent: CalendarEventUpdateRequest = {
    name: '',
    description: '',
    location: '',
    priority: CalendarEventPriority.Medium,
    calendar_id: -1,
    creator: {notifications: false},
    timing: {
      start_at: '',
      timezone: '',
      duration: 60,
      is_full_day: false,
      notify_before: null,
      notify_time: null,
    },
    recurrence: {
      rrule: '',
      deleted_occurrences: [],
    },
    override: {
      parent_id: null,
      original_at: null,
    },
    participants: {
      user_ids: [],
      group_ids: [],
      external_user_emails: [],
      all_users: false,
      notify_all_users: false,
      notify_user_ids: [],
      notify_group_ids: [],
    },
    inventories: []
  }
  return newEvent;
}

export function createCalendarEventResourceSeriesOverrideEvent(master: CalendarEventResource, start: Date, overrideTimezone?: string): CalendarEventResource {
  const overrideEvent: CalendarEventResource = JSON.parse(JSON.stringify(master));
  const timezoneHelper = new Timezone();
  overrideEvent.id = -1;
  overrideEvent.override.parent_id = master.id;
  const timezone = overrideTimezone || overrideEvent.timing.timezone || timezoneHelper.getLocalTimezoneName();
  overrideEvent.timing.timezone = timezone;
  const localizedStart = timezoneHelper.add(start, timezone);
  const year = localizedStart.getFullYear();
  const month = String(localizedStart.getMonth() + 1).padStart(2, '0');
  const day = String(localizedStart.getDate()).padStart(2, '0');
  overrideEvent.override.original_at = `${year}-${month}-${day}`;
  overrideEvent.timing.start_at = start.toISOString();
  overrideEvent.recurrence.rrule = '';
  overrideEvent.recurrence.deleted_occurrences = [];
  overrideEvent.recurrence.overridden_occurrences = [];
  overrideEvent.etag = '';
  return overrideEvent;
}

function mapInventoryResourceToInnerInventoryResource(inventoryItems: CalendarEventInventoryResource[]): CalendarEventInventoryValidationRequestInventoriesInner[] {
  return inventoryItems.map((item) => ({id: item.inventory.id, quantity: item.quantity}))
}
